import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import {
  AlertCircle,
  ChevronsDownUp,
  ChevronsUpDown,
  Inbox,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import { Button, ConnectionIndicator, Input } from '@/components/ui';
import { cn } from '@/lib/cn';
import { routes } from '@/lib/routes';
import { useInbox } from '@/hooks/useInbox';
import { useSpaceInbox } from '@/hooks/useSpaceInbox';
import { useAllLabels } from '@/hooks/useLabels';
import { useMeSpaces } from '@/hooks/useMeSpaces';
import { InboxLabelRail } from '@/components/inbox/InboxLabelRail';
import { InboxList } from '@/components/inbox/InboxList';
import { InboxListSkeleton } from '@/components/inbox/InboxListSkeleton';
import { InboxBulkActionBar } from '@/components/inbox/InboxBulkActionBar';
import type { InboxItem, InboxItemStatus } from '@/types/inbox';
import { cleanDisplayText } from '@/utils/display-text';

const FILTERS: { value: InboxItemStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'cancelled', label: 'Cancelled' },
];

function matchesQuery(item: InboxItem, q: string): boolean {
  return (
    cleanDisplayText(item.title).toLowerCase().includes(q) ||
    (item.agentName || '').toLowerCase().includes(q) ||
    (item.preview || '').toLowerCase().includes(q)
  );
}

export default function OperatorInbox() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // `?selected=` carries either a label id (`lbl-…`) or a space id
  // (`spc-…`). A label selection resolves its space from the shared
  // `useAllLabels` cache.
  const selectedParam = searchParams.get('selected');

  const [status, setStatus] = useState<InboxItemStatus>('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Multi-select for bulk actions. Distinct from `selectedId` (the keyboard
  // cursor) — Gmail/Linear pattern: `x` toggles the focused row, click the
  // hover-checkbox to pick with mouse.
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  // Resolve the selected id into space/label parts. A label selection
  // grabs its `spaceId` from the cache; a space selection uses itself.
  const { byId: labelsById } = useAllLabels();
  const selectedLabel =
    selectedParam && selectedParam.startsWith('lbl-')
      ? labelsById.get(selectedParam)
      : undefined;
  const resolvedLabelId = selectedLabel?.labelId;
  const resolvedSpaceId =
    selectedParam && selectedParam.startsWith('spc-')
      ? selectedParam
      : selectedLabel?.spaceId;
  const spaceScope = resolvedSpaceId
    ? { spaceId: resolvedSpaceId, label: resolvedLabelId }
    : null;

  // Two data sources: cross-space personal inbox by default; switch to the
  // space-scoped endpoint as soon as the URL pins us to a space (with or
  // without a label narrowing further).
  const personalInbox = useInbox({ status });
  const spaceInbox = useSpaceInbox({
    spaceId: spaceScope?.spaceId ?? '',
    label: spaceScope?.label,
    status,
  });
  const source = spaceScope ? spaceInbox : personalInbox;
  const {
    items,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    isConnected,
    diagnostics,
  } = source;

  // Spaces drive the active-space lookup for the breadcrumb. The rail
  // owns the space grouping and ordering now, so we don't sort here.
  const { data: spacesData } = useMeSpaces();
  const activeLabel = selectedLabel;
  const activeSpace = resolvedSpaceId
    ? (spacesData?.items ?? []).find((s) => s.spaceId === resolvedSpaceId)
    : undefined;

  // Hierarchical clear: a label selection peels back to its space first,
  // a bare space selection drops to "All items".
  const clearScope = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    if (selectedLabel) {
      next.set('selected', selectedLabel.spaceId);
    } else {
      next.delete('selected');
    }
    setSearchParams(next);
  }, [selectedLabel, searchParams, setSearchParams]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => matchesQuery(it, q));
  }, [items, searchQuery]);

  // Default selection to the first row; drop expanded/checked rows that
  // vanished from the filtered view (resolved, filter changed, search
  // narrowed) so the sets never hold IDs the user can't see.
  useEffect(() => {
    if (!selectedId && filteredItems.length > 0) {
      setSelectedId(filteredItems[0].inboxItemId);
    }
    if (selectedId && !filteredItems.some((x) => x.inboxItemId === selectedId)) {
      setSelectedId(filteredItems[0]?.inboxItemId ?? null);
    }
    setExpandedIds((prev) => {
      const live = new Set(filteredItems.map((x) => x.inboxItemId));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (live.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
    setCheckedIds((prev) => {
      const live = new Set(filteredItems.map((x) => x.inboxItemId));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (live.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [filteredItems, selectedId]);

  // `/` focuses the inbox search. Routed via react-hotkeys-hook so it
  // shows up in the Shift+? cheatsheet and inherits its built-in skip for
  // input/textarea/contentEditable targets.
  useHotkeys(
    'slash',
    (event) => {
      event.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    },
    {
      description: 'Focus inbox search',
      metadata: { group: 'Inbox' },
    },
  );

  // `Esc` escalates: when the selected row is expanded it collapses (handled
  // by InboxList's `←/Esc` binding); once nothing is expanded, the next `Esc`
  // focuses the search. The guard keeps the two behaviors from overlapping —
  // both Esc handlers fire, but only one acts depending on expansion state.
  // Shares the `/` handler's description so the cheatsheet renders one row.
  useHotkeys(
    'escape',
    (event) => {
      if (selectedId && expandedIds.has(selectedId)) return;
      event.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    },
    {
      description: 'Focus inbox search',
      metadata: { group: 'Inbox' },
    },
    [selectedId, expandedIds],
  );

  useHotkeys(
    'o',
    (event) => {
      if (!selectedId) return;
      const item = filteredItems.find((it) => it.inboxItemId === selectedId);
      if (!item) return;
      event.preventDefault();
      const href = item.surfaceId
        ? `${routes.operator.chat(item.conversationId)}?focus=${item.surfaceId}`
        : routes.operator.chat(item.conversationId);
      navigate(href);
    },
    {
      description: 'Open conversation',
      metadata: { group: 'Inbox' },
    },
    [selectedId, filteredItems, navigate],
  );

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandOne = useCallback((id: string) => {
    setExpandedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const collapseOne = useCallback((id: string) => {
    setExpandedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const toggleChecked = useCallback((id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearChecked = useCallback(() => setCheckedIds(new Set()), []);

  const selectAll = useCallback(
    () => setCheckedIds(new Set(filteredItems.map((x) => x.inboxItemId))),
    [filteredItems],
  );

  const checkedItems = useMemo(
    () => filteredItems.filter((it) => checkedIds.has(it.inboxItemId)),
    [filteredItems, checkedIds],
  );

  const anyExpanded = expandedIds.size > 0;

  const toggleAll = useCallback(() => {
    if (anyExpanded) {
      setExpandedIds(new Set());
    } else {
      setExpandedIds(new Set(filteredItems.map((x) => x.inboxItemId)));
    }
  }, [anyExpanded, filteredItems]);

  const expandLabel = useMemo(
    () => (anyExpanded ? 'Collapse all' : 'Expand all'),
    [anyExpanded],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Inbox</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Approval requests, clarifying questions, and delegated work from your agents.
          </p>
        </div>
        <div className="flex items-center gap-2 pr-2">
          <ConnectionIndicator
            isConnected={isConnected}
            diagnostics={diagnostics}
          />
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-rose-300/50 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-500/30 dark:bg-rose-950/30 dark:text-rose-100">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Couldn't load your inbox.</p>
            <p className="text-rose-800/80 dark:text-rose-200/80">{error.message}</p>
          </div>
        </div>
      )}

      {!error && (
        <>
          {/* Gmail-style: dedicated full-width search row above the bulk
              action bar. Stays visible even when the query has zero
              matches so the user can refine without leaving the surface. */}
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                // Esc blurs (so arrow keys can drive the list) without
                // clearing — the filter stays applied. Backspace or the
                // native search-clear control still empties the query.
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              placeholder="Search inbox…"
              aria-label="Search inbox"
              className="h-11 rounded-full bg-muted/60 pl-10 focus-visible:bg-background"
            />
          </div>

          <div className="flex gap-6">
            <InboxLabelRail />
            <div className="min-w-0 flex-1 space-y-6">
          {/* Bulk bar + list-or-empty-state share one vertical column so
              the empty/no-match card lands at the same Y as the list. The
              bar always reserves its height (invisible when not actionable)
              so neither the list nor the empty card jumps. The space-filter
              chips share this row, right-aligned via ml-auto. */}
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div
                className={
                  checkedItems.length > 0 ? '' : 'pointer-events-none invisible'
                }
                aria-hidden={checkedItems.length === 0}
              >
                <InboxBulkActionBar
                  selected={checkedItems}
                  total={filteredItems.length}
                  onClear={clearChecked}
                  onSelectAll={selectAll}
                  filter={{ status }}
                />
              </div>
              <div className="ml-auto flex items-center gap-4 pr-2">
                {spaceScope ? (
                  // Breadcrumb for the active scope. "Space" alone when
                  // picked from the rail heading; "Space › Label" when
                  // narrowed further. The ✕ peels label first, then space.
                  <div className="flex items-center gap-2 text-xs">
                    <span
                      className={cn(
                        activeLabel
                          ? 'text-muted-foreground'
                          : 'font-medium text-foreground',
                      )}
                    >
                      {activeSpace?.name ?? resolvedSpaceId}
                    </span>
                    {activeLabel && (
                      <>
                        <span className="text-muted-foreground/60">›</span>
                        <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                          {activeLabel.color && (
                            <span
                              className="h-1.5 w-1.5 rounded-full"
                              style={{ backgroundColor: activeLabel.color }}
                              aria-hidden
                            />
                          )}
                          {activeLabel.name}
                        </span>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={clearScope}
                      className="focus-ring rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label={activeLabel ? 'Clear label filter' : 'Clear space filter'}
                      title={activeLabel ? 'Clear label filter' : 'Clear space filter'}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : null}
                {filteredItems.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleAll}
                    title={expandLabel}
                  >
                    {anyExpanded ? (
                      <ChevronsDownUp className="mr-1.5 h-3.5 w-3.5" />
                    ) : (
                      <ChevronsUpDown className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    {expandLabel}
                  </Button>
                )}
                <div className="flex gap-1 rounded-md border border-border bg-card p-1">
                  {FILTERS.map((f) => (
                    <button
                      key={f.value}
                      type="button"
                      onClick={() => {
                        setStatus(f.value);
                        setSelectedId(null);
                        setExpandedIds(new Set());
                        setCheckedIds(new Set());
                      }}
                      className={
                        'focus-ring rounded px-3 py-1 text-xs font-medium transition-colors ' +
                        (status === f.value
                          ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200'
                          : 'text-muted-foreground hover:text-foreground')
                      }
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {isLoading && items.length === 0 ? (
              <InboxListSkeleton />
            ) : items.length === 0 ? (
              // No inbox items at all (for the current status / label /
              // space scope). Rendered inside the list slot so the rail,
              // search, and filters remain interactive.
              <EmptyState
                status={status}
                labelName={activeLabel?.name}
                spaceName={activeLabel ? undefined : activeSpace?.name}
              />
            ) : filteredItems.length === 0 ? (
              <NoSearchMatches
                query={searchQuery}
                onClear={() => setSearchQuery('')}
              />
            ) : (
              <InboxList
                items={filteredItems}
                selectedId={selectedId}
                expandedIds={expandedIds}
                checkedIds={checkedIds}
                onSelect={setSelectedId}
                onToggleExpand={toggleExpand}
                onExpand={expandOne}
                onCollapse={collapseOne}
                onToggleChecked={toggleChecked}
              />
            )}
          </div>
          {hasNextPage && filteredItems.length > 0 && (
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage && (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                )}
                Load more
              </Button>
            </div>
          )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function NoSearchMatches({
  query,
  onClear,
}: {
  query: string;
  onClear: () => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-12 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Search className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="mt-4 text-base font-medium text-foreground">
        No items match “{query}”
      </h3>
      <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
        Try a different search, or clear it to see the full inbox.
      </p>
      <div className="mt-6 flex justify-center">
        <Button variant="outline" size="sm" onClick={onClear}>
          Clear search
        </Button>
      </div>
    </div>
  );
}

const STATUS_COPY: Record<
  InboxItemStatus,
  { heading: string; body: string }
> = {
  pending: {
    heading: 'Nothing pending',
    body: 'When agents need approvals, ask clarifying questions, or finish delegated work, the items will show up here.',
  },
  resolved: {
    heading: 'Nothing resolved yet',
    body: 'Items you answer or that the agent finishes will move here.',
  },
  cancelled: {
    heading: 'Nothing cancelled',
    body: 'Items you dismiss or that the agent recalls will move here.',
  },
  expired: {
    heading: 'Nothing expired',
    body: 'Items that time out before being answered will move here.',
  },
};

function EmptyState({
  status,
  labelName,
  spaceName,
}: {
  status: InboxItemStatus;
  labelName?: string;
  spaceName?: string;
}) {
  // Both a label and a space filter use the same compact scoped empty state
  // (no call-to-action buttons); only the unscoped inbox shows the full copy.
  const scopeName = labelName ?? spaceName;
  const isScoped = !!scopeName;
  const copy = STATUS_COPY[status];
  return (
    <div className="rounded-lg border border-border bg-card p-12 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Inbox className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="mt-4 text-base font-medium text-foreground">
        {isScoped ? `No items in “${scopeName}”` : copy.heading}
      </h3>
      <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
        {labelName
          ? 'Items labelled this way will appear here when they arrive.'
          : spaceName
            ? 'Items in this space will appear here when they arrive.'
            : copy.body}
      </p>
      {!isScoped && (
        <div className="mt-6 flex justify-center gap-3">
          <Link to={routes.operator.chats}>
            <Button variant="outline">Open chat</Button>
          </Link>
          <Link to={routes.operator.builder}>
            <Button>Build a workflow</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
